// 自社プロモーション banner carousel — Issue #6629
// Scoped to a root element so multiple instances can coexist.
// `onSendGTag` is passed in by the caller so the caller controls which
// pack (desktop vs mobile) supplies the GTM bridge.

const EVENT_CATEGORY_IMPRESSION = "自社プロモーション_banner_impression";
const EVENT_CATEGORY_SLIDE_VIEW = "自社プロモーション_banner_slide_view";
const EVENT_CATEGORY_CLICK = "自社プロモーション_banner_click";
const ACTION_CLICK = "タップ";
const ACTION_SCROLL = "完読";
const AUTO_ROTATE_MS = 3000;
const SWIPE_THRESHOLD_PX = 50;

function escapeHtml(str) {
  return String(str).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );
}

export default function initCompanyPromotionBanner(root, onSendGTag, pattern) {
  if (!root) return;

  const configEl = root.querySelector(".js-company-promotion-config");
  const trackEl = root.querySelector(".js-company-promotion-track");
  const viewportEl = root.querySelector(".js-company-promotion-viewport");
  const dotsWrap = root.querySelector(".js-company-promotion-dots");
  const titleText = root.querySelector(".js-company-promotion-title-text");
  const moduleEl = root.querySelector(".js-company-promotion-module");

  if (
    !configEl ||
    !trackEl ||
    !viewportEl ||
    !dotsWrap ||
    !titleText ||
    !moduleEl
  )
    return;

  let parsed;
  try {
    parsed = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  // The AB pattern only selects the first slide; slides 2+ are shared across patterns.
  // Config shape: { firstSlideByPattern: { inhouse_1, inhouse_2 }, commonSlides: [...] }.
  // Fall back to a plain array for backward compatibility with cached SSR markup.
  let config;
  if (Array.isArray(parsed)) {
    config = parsed;
  } else {
    const firstSlides = parsed.firstSlideByPattern || {};
    const firstSlide = firstSlides[pattern] || firstSlides.inhouse_1;
    config = [firstSlide, ...(parsed.commonSlides || [])].filter(Boolean);
  }

  const totalSlides = config.length;
  if (totalSlides === 0) return;

  let current = 0;
  let autoTimer = null;

  const sendImpression = () => {
    onSendGTag(ACTION_SCROLL, {
      event_category: EVENT_CATEGORY_IMPRESSION,
      event_label: ACTION_SCROLL,
      non_interaction: true,
    });
  };

  const sendSlideView = (index) => {
    onSendGTag(ACTION_SCROLL, {
      event_category: EVENT_CATEGORY_SLIDE_VIEW,
      event_label: index,
      non_interaction: true,
    });
  };

  const sendClick = (index) => {
    onSendGTag(ACTION_CLICK, {
      event_category: EVENT_CATEGORY_CLICK,
      event_label: index,
    });
  };

  // ---- Render slides ----
  const pcMQ = window.matchMedia("(min-width: 768px)");
  const slideEntries = [];

  config.forEach((slide) => {
    const slideEl = document.createElement("div");
    slideEl.className = "companyPromotion-Slide";

    const hasImage = slide.imageUrlSP || slide.imageUrlPC || slide.imageUrl;

    const applyBg = () => {
      const url = pcMQ.matches
        ? slide.imageUrlPC || slide.imageUrlSP || slide.imageUrl || ""
        : slide.imageUrlSP || slide.imageUrlPC || slide.imageUrl || "";
      if (url) {
        slideEl.style.background = "";
        slideEl.style.backgroundImage = `url("${url}")`;
        slideEl.style.backgroundSize = slide.bgSize || "cover";
        slideEl.style.backgroundPosition = "center";
        slideEl.style.backgroundRepeat = "no-repeat";
      } else {
        slideEl.style.backgroundImage = "";
        slideEl.style.background = `linear-gradient(135deg, ${slide.gradientFrom} 0%, ${slide.gradientTo} 100%)`;
      }
    };

    applyBg();
    slideEntries.push({ slideEl, applyBg });

    if (!hasImage && slide.title) {
      slideEl.innerHTML = `
        <div class="companyPromotion-Slide_Text">
          <div class="companyPromotion-Slide_Title">${escapeHtml(slide.title || "")}</div>
          <div class="companyPromotion-Slide_Sub">${escapeHtml(slide.sub || "")}</div>
        </div>
      `;
    }
    trackEl.appendChild(slideEl);
  });

  // ---- Pixel-precise layout (avoids sub-pixel rounding gaps) ----
  const updateLayout = () => {
    const vw = viewportEl.getBoundingClientRect().width;
    if (!vw) return;
    trackEl.style.width = `${vw * totalSlides}px`;
    slideEntries.forEach(({ slideEl }) => {
      slideEl.style.width = `${vw}px`;
    });
    const wasTransition = trackEl.style.transition;
    trackEl.style.transition = "none";
    trackEl.style.transform = `translateX(${-vw * current}px)`;
    requestAnimationFrame(() => {
      trackEl.style.transition = wasTransition;
    });
  };

  updateLayout();
  window.addEventListener("resize", updateLayout);

  // Re-pick SP/PC image on breakpoint change
  const onMQChange = () =>
    slideEntries.forEach(({ applyBg }) => applyBg());
  if (typeof pcMQ.addEventListener === "function") {
    pcMQ.addEventListener("change", onMQChange);
  } else if (typeof pcMQ.addListener === "function") {
    pcMQ.addListener(onMQChange);
  }

  titleText.textContent = config[0].body;
  moduleEl.href = config[0].href;

  // ---- Navigation ----
  const goTo = (index) => {
    current = index;
    const vw = viewportEl.getBoundingClientRect().width;
    trackEl.style.transform = `translateX(${-vw * index}px)`;

    dotsWrap.querySelectorAll(".companyPromotion-Dots_Item").forEach((d, i) => {
      d.classList.toggle("is-active", i === index);
    });

    titleText.style.opacity = "0";
    setTimeout(() => {
      titleText.textContent = config[index].body;
      moduleEl.href = config[index].href;
      titleText.style.opacity = "1";
    }, 150);

    sendSlideView(index + 1);
  };

  // ---- Dots (only when 2+ slides) ----
  if (totalSlides >= 2) {
    config.forEach((_, i) => {
      const dot = document.createElement("div");
      dot.className = `companyPromotion-Dots_Item${i === 0 ? " is-active" : ""}`;
      dot.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        goTo(i);
        // eslint-disable-next-line no-use-before-define
        resetAuto();
      });
      dotsWrap.appendChild(dot);
    });
  } else {
    dotsWrap.classList.add("hidden");
  }

  // ---- Auto-rotate ----
  const startAuto = () => {
    if (totalSlides < 2 || autoTimer) return;
    autoTimer = setInterval(
      () => goTo((current + 1) % totalSlides),
      AUTO_ROTATE_MS,
    );
  };

  const stopAuto = () => {
    if (!autoTimer) return;
    clearInterval(autoTimer);
    autoTimer = null;
  };

  const resetAuto = () => {
    stopAuto();
    startAuto();
  };

  // ---- Swipe ----
  let touchStartX = 0;
  let touchStartY = 0;
  let touchMoved = false;

  viewportEl.addEventListener(
    "touchstart",
    (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchMoved = false;
    },
    { passive: true },
  );

  viewportEl.addEventListener(
    "touchmove",
    () => {
      touchMoved = true;
    },
    { passive: true },
  );

  viewportEl.addEventListener(
    "touchend",
    (e) => {
      if (!touchMoved || totalSlides < 2) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > SWIPE_THRESHOLD_PX) {
        const next =
          dx < 0
            ? Math.min(current + 1, totalSlides - 1)
            : Math.max(current - 1, 0);
        goTo(next);
        resetAuto();
      }
    },
    { passive: true },
  );

  let isVisible = false;
  let impressed = false;

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      stopAuto();
    } else if (isVisible) {
      startAuto();
    }
  });

  moduleEl.addEventListener("click", () => {
    sendClick(current + 1);
  });

  // Pause/resume auto-rotate as the banner enters/leaves the viewport.
  // Impression + initial slide_view fire once, on first entry.
  if (typeof IntersectionObserver === "function") {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisible = entry.isIntersecting;
          if (entry.isIntersecting && !impressed) {
            impressed = true;
            sendImpression();
            sendSlideView(1);
          }
          if (entry.isIntersecting) {
            startAuto();
          } else {
            stopAuto();
          }
        });
      },
      { threshold: 0.5 },
    );
    observer.observe(moduleEl);
  } else {
    isVisible = true;
    impressed = true;
    sendImpression();
    sendSlideView(1);
    startAuto();
  }
}
